import { useRef, useState, type ChangeEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, ChevronDown, ChevronUp, FileUp, ScanSearch } from "lucide-react";
import { fetchImportItems, fetchLeadById } from "@/lib/leads";
import { importarLote, resolverDuplicata } from "@/lib/importacao";
import { parseTextoColado, type ImportParseResultado } from "@/lib/importParser";
import { extrairTextoDePdf } from "@/lib/pdfImport";
import { formatDate } from "@/lib/format";
import { LoadingState } from "@/components/common/States";
import type { ImportItem, ImportarLoteResumo } from "@/lib/types";

const STATUS_INFO: Record<
  ImportItem["status"],
  { label: string; tone: "success" | "warning" | "danger" | "neutral" }
> = {
  importado: { label: "Novo", tone: "success" },
  duplicata_suspeita: { label: "Duplicata", tone: "warning" },
  invalido: { label: "Erro", tone: "danger" },
  bloqueado_nao_contatar: { label: "Bloqueado (não contatar)", tone: "danger" },
  mesclado: { label: "Mesclado", tone: "neutral" },
  descartado: { label: "Ignorado", tone: "neutral" },
};

export function ImportarLoteForm({ onConcluido }: { onConcluido: () => void }) {
  const queryClient = useQueryClient();

  const [nome, setNome] = useState("");
  const [origemFonte, setOrigemFonte] = useState("");
  const [origemDetalhe, setOrigemDetalhe] = useState("");
  const [origemColetadaEm, setOrigemColetadaEm] = useState(() => new Date().toISOString().slice(0, 10));
  const [observacao, setObservacao] = useState("");
  const [texto, setTexto] = useState("");
  const [parse, setParse] = useState<ImportParseResultado | null>(null);
  const [batchId, setBatchId] = useState<string | null>(null);
  const [resumo, setResumo] = useState<ImportarLoteResumo | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [extraindoPdf, setExtraindoPdf] = useState(false);
  const inputPdfRef = useRef<HTMLInputElement>(null);

  const itemsQuery = useQuery({
    queryKey: ["importItems", batchId],
    queryFn: () => fetchImportItems(batchId as string),
    enabled: Boolean(batchId),
  });

  const importarMutation = useMutation({
    mutationFn: () =>
      importarLote({
        nome: nome.trim(),
        origem_fonte: origemFonte.trim(),
        origem_detalhe: origemDetalhe.trim(),
        origem_coletada_em: origemColetadaEm,
        observacao: observacao.trim() || null,
        linhas: parse?.linhas ?? [],
      }),
    onSuccess: (data) => {
      setResumo(data);
      setBatchId(data.batch_id);
    },
    onError: (e: unknown) => setErro((e as Error).message),
  });

  const camposObrigatoriosPreenchidos =
    nome.trim() !== "" && origemFonte.trim() !== "" && origemDetalhe.trim() !== "" && origemColetadaEm !== "";

  function handlePreVisualizar() {
    setErro(null);
    setParse(parseTextoColado(texto));
  }

  async function handleArquivoPdf(e: ChangeEvent<HTMLInputElement>) {
    const arquivo = e.target.files?.[0];
    e.target.value = ""; // permite selecionar o mesmo arquivo de novo depois
    if (!arquivo) return;

    setErro(null);
    setExtraindoPdf(true);
    try {
      const extraido = await extrairTextoDePdf(arquivo);
      setTexto(extraido);
      setParse(parseTextoColado(extraido));
    } catch {
      setErro("Não foi possível ler o PDF. Confirme que é um PDF de texto (não uma imagem escaneada).");
    } finally {
      setExtraindoPdf(false);
    }
  }

  if (resumo && batchId) {
    return (
      <div>
        <p>
          Lote <strong>{resumo.nome}</strong>: {resumo.total_linhas} linha(s) — {resumo.total_importados} nova(s),{" "}
          {resumo.total_duplicatas} duplicata(s), {resumo.total_bloqueados} bloqueada(s), {resumo.total_invalidos}{" "}
          com erro.
        </p>

        {itemsQuery.isLoading && <LoadingState label="Carregando itens do lote…" />}

        {itemsQuery.data && (
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {itemsQuery.data.map((item) => (
              <ImportItemRow
                key={item.id}
                item={item}
                onResolvido={() => {
                  queryClient.invalidateQueries({ queryKey: ["importItems", batchId] });
                  queryClient.invalidateQueries({ queryKey: ["kanban"] });
                  queryClient.invalidateQueries({ queryKey: ["filaHoje"] });
                  queryClient.invalidateQueries({ queryKey: ["leadsSearch"] });
                }}
              />
            ))}
          </ul>
        )}

        <div className="modal__footer" style={{ padding: "16px 0 0" }}>
          <button type="button" className="btn btn--primary" onClick={onConcluido}>
            Concluir
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="form-row">
        <div className="field">
          <label htmlFor="lote-nome">Nome do lote *</label>
          <input id="lote-nome" type="text" value={nome} onChange={(e) => setNome(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="lote-data">Data de coleta *</label>
          <input
            id="lote-data"
            type="date"
            value={origemColetadaEm}
            max={new Date().toISOString().slice(0, 10)}
            onChange={(e) => setOrigemColetadaEm(e.target.value)}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="field">
          <label htmlFor="lote-fonte">Fonte do dado *</label>
          <input
            id="lote-fonte"
            type="text"
            placeholder="Ex.: Google Maps, indicação, feira X"
            value={origemFonte}
            onChange={(e) => setOrigemFonte(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="lote-detalhe">Detalhe da origem (URL/descrição) *</label>
          <input
            id="lote-detalhe"
            type="text"
            placeholder="URL da lista ou descrição de como os dados foram obtidos"
            value={origemDetalhe}
            onChange={(e) => setOrigemDetalhe(e.target.value)}
          />
        </div>
      </div>

      <div className="field">
        <label htmlFor="lote-observacao">Observação (opcional)</label>
        <textarea id="lote-observacao" rows={2} value={observacao} onChange={(e) => setObservacao(e.target.value)} />
      </div>

      <div className="field">
        <label htmlFor="lote-texto">Colar dados (primeira linha = cabeçalho)</label>
        <p className="field-hint">
          Cabeçalhos reconhecidos: empresa, ramo, cidade, uf, telefone, pessoa_contato, email, site, prioridade,
          tags. Cole direto de uma planilha (tab) ou separado por vírgula/ponto e vírgula — ou importe um PDF com
          essa mesma tabela abaixo.
        </p>
        <textarea
          id="lote-texto"
          rows={8}
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder={"empresa\tcidade\ttelefone\nAcme Ltda\tBelo Horizonte\t(31) 90000-0000"}
        />
        <input
          ref={inputPdfRef}
          type="file"
          accept="application/pdf"
          hidden
          onChange={(e) => void handleArquivoPdf(e)}
        />
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          style={{ marginTop: 8 }}
          onClick={() => inputPdfRef.current?.click()}
          disabled={extraindoPdf}
        >
          <FileUp className="icon" size={16} aria-hidden="true" />
          {extraindoPdf ? "Lendo PDF…" : "Importar de um PDF"}
        </button>
      </div>

      <button type="button" className="btn btn--secondary" onClick={handlePreVisualizar} disabled={texto.trim() === ""}>
        <ScanSearch className="icon" size={16} aria-hidden="true" />
        Pré-visualizar
      </button>

      {parse && parse.avisos.length > 0 && (
        <div className="field-error" role="alert" style={{ marginTop: 12 }}>
          {parse.avisos.map((a) => (
            <p key={a}>{a}</p>
          ))}
        </div>
      )}

      {parse && parse.linhas.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <p>
            {parse.linhas.length} linha(s) reconhecida(s). Colunas usadas:{" "}
            {parse.colunasReconhecidas.join(", ") || "nenhuma"}.
            {parse.colunasIgnoradas.length > 0 && ` Ignoradas: ${parse.colunasIgnoradas.join(", ")}.`}
          </p>
          <div className="table-wrap" style={{ maxHeight: 240, overflowY: "auto" }}>
            <table>
              <thead>
                <tr>
                  {Object.keys(parse.linhas[0]).map((k) => (
                    <th key={k} scope="col">
                      {k}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {parse.linhas.slice(0, 50).map((linha, idx) => (
                  <tr key={idx}>
                    {Object.keys(parse!.linhas[0]).map((k) => (
                      <td key={k}>{String(linha[k] ?? "")}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {parse.linhas.length > 50 && <p className="field-hint">Mostrando as primeiras 50 de {parse.linhas.length}.</p>}
        </div>
      )}

      {erro && (
        <p className="field-error" role="alert">
          {erro}
        </p>
      )}

      <div className="modal__footer" style={{ padding: "16px 0 0" }}>
        <button
          type="button"
          className="btn btn--primary"
          disabled={
            !parse || parse.linhas.length === 0 || !camposObrigatoriosPreenchidos || importarMutation.isPending
          }
          onClick={() => importarMutation.mutate()}
        >
          {importarMutation.isPending ? "Importando…" : `Importar ${parse?.linhas.length ?? 0} linha(s)`}
        </button>
      </div>
    </div>
  );
}

function ImportItemRow({ item, onResolvido }: { item: ImportItem; onResolvido: () => void }) {
  const [mostrarComparacao, setMostrarComparacao] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const leadExistenteQuery = useQuery({
    queryKey: ["lead", item.match_lead_id],
    queryFn: () => fetchLeadById(item.match_lead_id as string),
    enabled: mostrarComparacao && Boolean(item.match_lead_id),
  });

  const resolverMutation = useMutation({
    mutationFn: (acao: "criar_novo" | "descartar") => resolverDuplicata(item.id, acao),
    onSuccess: onResolvido,
    onError: (e: unknown) => setErro((e as Error).message),
  });

  const info = STATUS_INFO[item.status];
  const payload = item.payload as Record<string, unknown>;
  const jaResolvido = item.resolvido_em !== null || item.status === "importado";

  return (
    <li className="card" style={{ marginBottom: 8 }}>
      <div className="utility-row" style={{ marginBottom: 4 }}>
        <span className={`badge badge--${info.tone}`}>{info.label}</span>
        <strong>{String(payload.empresa ?? "(sem empresa)")}</strong>
        <span className="field-hint">Linha {item.linha_num}</span>
      </div>

      {item.status === "invalido" && (
        <p className="field-error icon-text">
          <AlertCircle className="icon" size={14} aria-hidden="true" />
          <span>{item.erro}</span>
        </p>
      )}
      {item.status === "bloqueado_nao_contatar" && (
        <p className="field-error icon-text">
          <AlertCircle className="icon" size={14} aria-hidden="true" />
          <span>Consta na lista de não contatar — criação bloqueada.</span>
        </p>
      )}

      {item.status === "duplicata_suspeita" && !jaResolvido && (
        <>
          <p className="field-hint">
            Possível duplicata por {item.motivo_match === "telefone" ? "telefone" : "empresa + cidade"}.
          </p>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => setMostrarComparacao((v) => !v)}
            aria-expanded={mostrarComparacao}
          >
            {mostrarComparacao ? "Ocultar" : "Ver lead existente"}
            {mostrarComparacao ? (
              <ChevronUp className="icon" size={16} aria-hidden="true" />
            ) : (
              <ChevronDown className="icon" size={16} aria-hidden="true" />
            )}
          </button>

          {mostrarComparacao && (
            <div className="form-row" style={{ marginTop: 8 }}>
              <div>
                <h4>Linha importada</h4>
                <p>Empresa: {String(payload.empresa ?? "—")}</p>
                <p>Telefone: {String(payload.telefone ?? "—")}</p>
                <p>Cidade: {String(payload.cidade ?? "—")}</p>
              </div>
              <div>
                <h4>Lead existente</h4>
                {leadExistenteQuery.isLoading && <LoadingState label="Carregando…" />}
                {leadExistenteQuery.data && (
                  <>
                    <p>Empresa: {leadExistenteQuery.data.empresa}</p>
                    <p>Telefone: {leadExistenteQuery.data.telefone ?? "—"}</p>
                    <p>Cidade: {leadExistenteQuery.data.cidade ?? "—"}</p>
                    <p>Criado em: {formatDate(leadExistenteQuery.data.criado_em)}</p>
                  </>
                )}
              </div>
            </div>
          )}

          {erro && (
            <p className="field-error" role="alert">
              {erro}
            </p>
          )}

          <div className="fila-item__actions" style={{ marginTop: 8 }}>
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              disabled={resolverMutation.isPending}
              onClick={() => resolverMutation.mutate("criar_novo")}
            >
              Importar linha
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={resolverMutation.isPending}
              onClick={() => resolverMutation.mutate("descartar")}
            >
              Ignorar linha
            </button>
          </div>
        </>
      )}

      {item.status === "bloqueado_nao_contatar" && item.resolvido_em === null && (
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          disabled={resolverMutation.isPending}
          onClick={() => resolverMutation.mutate("descartar")}
        >
          Marcar como revisado
        </button>
      )}

      {item.resolvido_em && item.status !== "importado" && (
        <p className="field-hint">Resolvido: {STATUS_INFO[item.status].label}</p>
      )}
    </li>
  );
}
